import React, { useState, useEffect, useCallback, useRef } from 'react';
import { COLORS } from '../utils/constants';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { tradingAPI } from '../services/api';
import websocketService from '../services/websocket';
import { useLanguage } from '../context/LanguageContext';

import TradingChart from '../components/trading/TradingChart';
import OrderBook from '../components/trading/OrderBook';
import PositionsList from '../components/trading/PositionsList';
import TradeHistory from '../components/trading/TradeHistory';
import PendingOrders from '../components/trading/PendingOrders';
import MarketTrades from '../components/trading/MarketTrades';
import SpotAssetsPanel from '../components/trading/SpotAssetsPanel';
import PremiumMobileTradingPage from '../components/mobile/PremiumMobileTradingPage';
import TradingHeader from '../components/trading/TradingHeader';
import TradeModal from '../components/trading/TradeModal';
import SLTPModal from '../components/trading/SLTPModal';
import SpotSellModal from '../components/trading/SpotSellModal';
import SpotBuyModal from '../components/trading/SpotBuyModal';
import { 
  Wifi, WifiOff, TrendingUp, TrendingDown
} from 'lucide-react';

const TradingPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSymbol = searchParams.get('symbol');
  const [selectedSymbol, setSelectedSymbol] = useState(urlSymbol || 'BTCUSDT');
  const [currentPrice, setCurrentPrice] = useState(0);
  const [ticker, setTicker] = useState(null);
  const [refreshPositions, setRefreshPositions] = useState(0);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [activeBottomTab, setActiveBottomTab] = useState('positions');
  const [tradeMode, setTradeMode] = useState('futures');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [positions, setPositions] = useState([]);
  const [sltpModal, setSltpModal] = useState({ isOpen: false, trade: null });
  const [sellModal, setSellModal] = useState({ isOpen: false, holding: null });
  const [buyModal, setBuyModal] = useState({ isOpen: false, holding: null });
  const [refreshSpotAssets, setRefreshSpotAssets] = useState(0);

  // Load positions for header stats
  useEffect(() => {
    const loadPositions = async () => {
      try {
        const res = await tradingAPI.getTrades('open');
        setPositions(res.data || []);
      } catch (err) {
        console.error('Failed to load positions:', err);
      }
    };
    loadPositions();
    const interval = setInterval(loadPositions, 3000);
    return () => clearInterval(interval);
  }, [refreshPositions]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (selectedSymbol && selectedSymbol !== urlSymbol) {
      setSearchParams({ symbol: selectedSymbol }, { replace: true });
    }
  }, [selectedSymbol, urlSymbol, setSearchParams]);

  const handleTickerUpdate = useCallback((message) => {
    if (message.type === 'ticker' && message.data) {
      const data = message.data;
      setCurrentPrice(data.price);
      setTicker({
        lastPrice: data.price,
        priceChange: data.change_day || 0,
        priceChangePercent: data.change_24h || 0,
        highPrice: data.high_24h || data.price,
        lowPrice: data.low_24h || data.price,
        volume: data.volume_24h || 0,
        quoteVolume: data.volume_usd_24h || 0,
        source: data.source,
      });
      setLastUpdate(new Date());
      setWsConnected(true);
    }
  }, []);

  useEffect(() => {
    loadMarketData();
    websocketService.connectTicker(
      selectedSymbol,
      handleTickerUpdate,
      (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      }
    );
    return () => {};
  }, [selectedSymbol, handleTickerUpdate]);

  useEffect(() => {
    if (!wsConnected) {
      const interval = setInterval(loadMarketData, 5000);
      return () => clearInterval(interval);
    }
  }, [wsConnected, selectedSymbol]);

  const loadMarketData = async () => {
    try {
      const [priceRes, tickerRes] = await Promise.all([
        tradingAPI.getPrice(selectedSymbol),
        tradingAPI.getTicker(selectedSymbol),
      ]);
      setCurrentPrice(priceRes.data.price);
      setTicker(tickerRes.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to load market data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTradeOpened = () => {
    setRefreshPositions((prev) => prev + 1);
    setRefreshSpotAssets((prev) => prev + 1);  // Also refresh spot assets
  };

  const handleAssetSelect = (symbol) => {
    setSelectedSymbol(symbol);
  };

  const handleSellAsset = (holding) => {
    setSellModal({ isOpen: true, holding });
  };

  const handleBuyAsset = (holding) => {
    setBuyModal({ isOpen: true, holding });
  };

  const handleTradeModeChange = (mode) => {
    setTradeMode(mode);
  };

  const formatVolume = (value) => {
    if (value >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
    if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
    return value?.toFixed(2) || '0';
  };

  const priceChangePercent = ticker?.priceChangePercent || 0;
  const isPositive = priceChangePercent >= 0;
  const baseAsset = selectedSymbol.replace('USDT', '');

  // ==================== PREMIUM MOBILE LAYOUT ====================
  if (isMobile) {
    return (
      <PremiumMobileTradingPage
        selectedSymbol={selectedSymbol}
        setSelectedSymbol={setSelectedSymbol}
        onTradeOpened={handleTradeOpened}
      />
    );
  }

  // ==================== DESKTOP LAYOUT ====================
  return (
    <div style={{ 
      backgroundColor: COLORS.background, 
      height: 'calc(100vh - 64px)', 
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Trading Header - directly under main header */}
      <TradingHeader
        selectedSymbol={selectedSymbol}
        onSelectSymbol={setSelectedSymbol}
        currentPrice={currentPrice}
        ticker={ticker}
        wsConnected={wsConnected}
        onOpenTradeModal={() => setShowTradeModal(true)}
        tradeMode={tradeMode}
        setTradeMode={setTradeMode}
        positions={positions}
        refreshTrigger={refreshSpotAssets}
      />

      {/* Main Grid */}
      <div style={{ padding: '6px 12px 12px', flex: 1, overflow: 'hidden' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 280px',
          gridTemplateRows: '1fr 1fr',
          gap: '6px',
          height: '100%',
        }}>
          {/* Chart - Full width top left */}
          <div style={{
            backgroundColor: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '12px',
            overflow: 'hidden',
            gridRow: '1',
            gridColumn: '1',
          }}>
            <TradingChart symbol={selectedSymbol} />
          </div>

          {/* OrderBook - Right side top */}
          <div style={{
            backgroundColor: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '12px',
            overflow: 'hidden',
            gridRow: '1',
            gridColumn: '2',
          }}>
            <OrderBook symbol={selectedSymbol} />
          </div>

          {/* Positions/Orders/History - Bottom left */}
          <div style={{
            backgroundColor: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '12px',
            overflow: 'hidden',
            gridRow: '2',
            gridColumn: '1',
            display: 'flex',
            flexDirection: 'column',
          }}>
            {tradeMode === 'futures' ? (
              <>
                <div style={{ 
                  display: 'flex', 
                  borderBottom: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.background,
                  borderRadius: '12px 12px 0 0',
                  flexShrink: 0,
                }}>
                  {[
                    { id: 'positions', label: t('positions') },
                    { id: 'orders', label: t('orders') },
                    { id: 'history', label: t('history') },
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveBottomTab(tab.id)}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderBottom: `3px solid ${activeBottomTab === tab.id ? COLORS.primary : 'transparent'}`,
                        color: activeBottomTab === tab.id ? COLORS.primary : COLORS.textSecondary,
                        fontSize: '13px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                  {activeBottomTab === 'positions' && (
                    <PositionsList 
                      symbol={null} 
                      refresh={refreshPositions} 
                      embedded 
                      onSetSLTP={(trade) => setSltpModal({ isOpen: true, trade })}
                    />
                  )}
                  {activeBottomTab === 'orders' && (
                    <PendingOrders embedded onOrderCancelled={handleTradeOpened} />
                  )}
                  {activeBottomTab === 'history' && (
                    <TradeHistory embedded />
                  )}
                </div>
              </>
            ) : (
              <SpotAssetsPanel 
                onSelectAsset={handleAssetSelect} 
                currentSymbol={selectedSymbol}
                onSellAsset={handleSellAsset}
                onBuyAsset={handleBuyAsset}
                refreshTrigger={refreshSpotAssets}
              />
            )}
          </div>

          {/* Market Trades - Bottom right */}
          <div style={{
            backgroundColor: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '12px',
            overflow: 'hidden',
            gridRow: '2',
            gridColumn: '2',
          }}>
            <MarketTrades symbol={selectedSymbol} />
          </div>
        </div>
      </div>

      {/* Trade Modal */}
      <TradeModal
        isOpen={showTradeModal}
        onClose={() => setShowTradeModal(false)}
        symbol={selectedSymbol}
        currentPrice={currentPrice}
        tradeMode={tradeMode}
        onSuccess={handleTradeOpened}
      />

      {/* SL/TP Modal */}
      <SLTPModal
        isOpen={sltpModal.isOpen}
        onClose={() => setSltpModal({ isOpen: false, trade: null })}
        trade={sltpModal.trade}
        onUpdate={() => setRefreshPositions(prev => prev + 1)}
      />

      {/* Spot Sell Modal */}
      <SpotSellModal
        isOpen={sellModal.isOpen}
        onClose={() => setSellModal({ isOpen: false, holding: null })}
        holding={sellModal.holding}
        onSuccess={handleTradeOpened}
      />

      {/* Spot Buy Modal */}
      <SpotBuyModal
        isOpen={buyModal.isOpen}
        onClose={() => setBuyModal({ isOpen: false, holding: null })}
        holding={buyModal.holding}
        onSuccess={handleTradeOpened}
      />

      {/* Animation styles */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default TradingPage;
